/**
 * A cited phrase in the answer.
 *
 * Visually part of the sentence — an underline, not a link colour. A click
 * takes the reader to ONE place, and which place depends on what the citation
 * can show them:
 *
 * - **A single PDF page** opens in the viewer, at that page with the excerpt
 *   highlighted, and nothing is appended to the citation chain. The drawer
 *   sliding in beside the document would be two surfaces answering one click —
 *   the noise this ticket exists to remove — and the viewer already shows the
 *   source in full.
 * - **Everything else** appends its card to the chain and leaves the reader in
 *   the prose: an Excel cell, a Slack message, a CRM field, and any PDF
 *   citation too ambiguous to open (several pages, several documents). There
 *   the chain is the only destination, so it must keep working exactly as it
 *   did — including on surfaces with no viewer mounted.
 *
 * Lives apart from `AssistantMessage` so it is the same component in the chat
 * bubble and under test. The bubble's markdown `a:` override does nothing more
 * than resolve the href and delegate here.
 */

import React, { useCallback, useMemo, useState } from "react";

import { usePdfViewer } from "../../context/PdfViewerContext";
import {
	pdfCitationTarget,
	type ResolvedCitation,
} from "../../utils/citationAnchors";
import { useCitationChain } from "./CitationChainContext";

export interface CitationAnchorProps {
	citation: ResolvedCitation;
	href?: string;
	children?: React.ReactNode;
}

export const CitationAnchor: React.FC<CitationAnchorProps> = ({
	citation,
	href,
	children,
}) => {
	const chain = useCitationChain();
	// Nullable on purpose: surfaces with no viewer mounted (exports, the shared
	// view) keep the card-only behaviour rather than opening a stray tab.
	const pdfViewer = usePdfViewer();
	const pdf = useMemo(() => pdfCitationTarget(citation), [citation]);
	const opensDocument = pdf !== null && pdfViewer !== null;
	// The opened state used to be read off chain membership alone. A citation
	// that opens the viewer never joins the chain, so its mark is tracked here
	// instead. Only the viewer path sets it: a card the reader later dismisses
	// must un-mark its anchor, which chain membership already handles.
	const [visited, setVisited] = useState(false);

	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			const opened = pdf
				? (pdfViewer?.openPdfHref(pdf.href, { filename: pdf.filename }) ?? false)
				: false;
			// The chain is the fallback destination, not a companion: it gets the
			// card only when the viewer could not take the reader anywhere.
			if (opened) {
				setVisited(true);
				return;
			}
			chain?.open({
				key: citation.key,
				event: citation.event,
				target: citation.target,
			});
		},
		[chain, citation, pdf, pdfViewer],
	);

	const isOpen =
		visited || chain?.chain.some((entry) => entry.key === citation.key);

	return (
		<a
			href={href}
			onClick={handleClick}
			className="markdown-citation-anchor"
			data-citation-key={citation.key}
			data-citation-open={isOpen ? "true" : undefined}
			aria-label={
				opensDocument
					? "Open the cited page"
					: "Show this citation in the audit log"
			}
		>
			{children}
		</a>
	);
};

export default CitationAnchor;
